/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import Auth from "../../shared/Auth";
import { qreq } from "../../shared/qrequest";
import BaseCom from "../BaseCom";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import './Dashboard.css';
import CM2PerDayChart from "../chart/CM2PerDayChart";
import CM2PerMonthChart from "../chart/CM2PerMonthChart";
import PhaseChart from "../chart/PhaseChart";
import LifecycleDash from "../chart/LifecycleDash";
import Globals from "../../shared/Globals";
import Card from "../Card";
import OrderProducts from "../OrderProducts";
import WoundTypeBreakdownChart from "../chart/WoundTypeBreakdownChart";
import Roundup from "./Roundup";
import { Link, Navigate } from "react-router-dom";
import GenDashboard from "./GenDashboard";
import MnfDashboard from "./MnfDashboard";
import ClaimStatusChart from "../chart/ClaimStatusChart";
import L from "../Lang";
import LabDashboard from "./LabDashboard";

export class CardPrimary extends BaseCom {
    render() {
        return <div className={`dashboard-panel dashboard-panel-primary ${this.props.className ? ' ' + this.props.className : ''}`}>
            <div className="card-header"><h3 className="dashboard-panel-title">{this.props.title}</h3></div>
            <div className="card-body">
                {this.props.children}
            </div>
        </div>;
    }
}

export class CardSecondary extends BaseCom {
    render() {
        return <div className={'dashboard-panel dashboard-panel-secondary' + (this.props.className ? ' ' + this.props.className : '')}>
            <div className="card-header"><h3 className="dashboard-panel-title">{this.props.title}</h3></div>
            <div className="card-body">
                {this.props.children}
            </div>
        </div>;
    }
}

class Meters extends BaseCom {
    componentDidMount() {
        qreq.get('/api/dashboard/meter/list', j => {
            this.setState({ meters: Array.isArray(j?.list) ? j.list : [], metersInvalid: !Array.isArray(j?.list) });
        });
    }

    render() {
        if (!this.state.meters) return <div className="dashboard-widget-state" role="status"><L>Loading overview…</L></div>;
        if (this.state.metersInvalid) return <div className="dashboard-widget-state" role="status"><L>Overview unavailable.</L></div>;
        if (!this.state.meters.length) return <div className="dashboard-widget-state"><L>No overview data yet.</L></div>;
        return <div className="meter-list">
            {this.state.meters?.map((i, index) => <div className="meter" key={i.id ?? `${i.label}-${index}`}>
                <div className="counter">{i.counter}</div>
                <div className="label">{i.label}</div>
            </div>)}
        </div>;
    }
}

class ProvidersWidget extends BaseCom {
    componentDidMount() {
        qreq.get('/api/provider/team/list', j => {
            this.setState({ list: Array.isArray(j?.list) ? j.list : [], providersInvalid: !Array.isArray(j?.list) });
        });
    }

    render() {
        if (!this.state.list) return <div className="dashboard-widget-state" role="status"><L>Loading care team…</L></div>;
        if (this.state.providersInvalid) return <div className="dashboard-widget-state" role="status"><L>Care team unavailable.</L></div>;
        if (!this.state.list.length) return <div className="dashboard-widget-state"><L>No care team providers yet.</L></div>;
        return <div className="providers-widget">
            {this.state.list?.map((i, index) => <div className="card-white d-flex align-items-center" key={i.id ?? `${i.firstName}-${i.lastName}-${index}`}>
                <div className="avatar" aria-hidden="true">
                    <span>{`${i.firstName?.charAt(0) || ''}${i.lastName?.charAt(0) || ''}` || '?'}</span>
                    {i.photoURL && <img src={Globals.getImageURL(i.photoURL)} alt="" loading="lazy"
                        onError={event => { event.currentTarget.style.display = 'none'; }} />}
                </div>
                <div className="info ms-2">
                    <div className="name">{i.firstName} {i.lastName}</div>
                    <div className="specialty text-muted">{i.specialty}</div>
                </div>
            </div>)}
        </div>;
    }

}

class NewDashboard extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            user: null
        };
    }

    async componentDidMount() {
        Auth.getLogin(u => {
            this.setState({ user: u });
        });
        await this.getSettings();
    }

    render() {
        if (this.state.MODEL === 'GEN')
            return <GenDashboard />;
        else if (this.state.MODEL === 'MNF')
            return <MnfDashboard />;
        else if (this.state.MODEL === 'MST')
            return <Navigate to="/instance" replace />;

        return <div className="Dashboard dashboard-redesign container-fluid">
            <h1><L>DASHBOARD</L></h1>
            <header className="dashboard-welcome">
                <div>
                    <div className="dashboard-greeting" role="heading" aria-level="1"><L>{Globals.getTimeOfDay('Good ')}</L>{this.state.user?.name ? `, ${this.state.user.name}` : ''}.</div>
                    <p><L>Your workspace at a glance.</L></p>
                </div>
            </header>
            {this.state.MODEL === 'WMS' && <>
                <div className="row">
                    <div className="col-xl-9 col-lg-8 mt-3">
                        <CardSecondary title={<L>Dashboard Overview</L>} className="dashboard-overview">
                            <Meters />
                        </CardSecondary>
                        {(this.state.MODELPROPS?.indexOf('WOUND') >= 0 || this.state.MODELPROPS?.indexOf('LYMPH') >= 0) && Auth.hasRole('manager') && <CardPrimary title={<L>Roundup</L>} className="mt-3">
                            <div className="p-2">
                                <Roundup />
                            </div>
                        </CardPrimary>}

                        {this.state.MODELPROPS?.indexOf('WOUND') >= 0 && <div className="row">
                            <div className="col-lg mt-3">
                                <CardPrimary title={<><L>Wounds by Type</L></>}>
                                    <WoundTypeBreakdownChart />
                                </CardPrimary>
                            </div>
                            <div className="col-lg mt-3">
                                <CardPrimary title={<><L>Total Patients in Phase</L></>}>
                                    <PhaseChart />
                                </CardPrimary>
                            </div>
                        </div>}
                        {Auth.hasRole('admin') && !this.state.hideClaimCharts && <div className="row">
                            <div className="col-lg-6 mt-3">
                                <CardPrimary title={<><L>Claim Count by Status</L></>}>
                                    <ClaimStatusChart type="count" onNoData={() => this.setState({ hideClaimCharts: true })} />
                                </CardPrimary>
                            </div>
                            <div className="col-lg-6 mt-3">
                                <CardPrimary title={<><L>Claim Amount by Status</L></>}>
                                    <ClaimStatusChart type="amount" onNoData={() => this.setState({ hideClaimCharts: true })} />
                                </CardPrimary>
                            </div>
                        </div>}
                    </div>
                    <div className="col-xl-3 col-lg-4 mt-3 dashboard-sidebar">
                        <CardSecondary title={<L>Care Team Providers</L>}>
                            <ProvidersWidget />
                        </CardSecondary>
                        <CardSecondary title={<L>Resources</L>} className="mt-3">
                            <div className="d-grid gap-1">
                                <Link to="/asset-library" className="btn btn-lg btn-primary"><L>Asset Library</L></Link>
                            </div>
                        </CardSecondary>
                    </div>
                </div>

                {this.state.MODELPROPS?.indexOf('WOUND') >= 0 && <div className="row">
                    <div className="col-lg mt-3">
                        <CardPrimary title={<><L>Total cm</L><sup><L>2</L></sup> per day (30 days)</>}>
                            <CM2PerDayChart />
                        </CardPrimary>
                    </div>
                    <div className="col-lg mt-3">
                        <CardPrimary title={<><L>Total cm</L><sup><L>2</L></sup> <L>per month</L></>}>
                            <CM2PerMonthChart />
                        </CardPrimary>
                    </div>
                </div>}

                {(this.state.MODELPROPS?.indexOf('WOUND') >= 0 || this.state.MODELPROPS?.indexOf('LYMPH') >= 0) && <>
                    <div className="row">
                        <div className="col-lg-12 mt-3">
                            <LifecycleDash />
                        </div>
                    </div>

                    <Card title="Products" className="mt-3">
                        <OrderProducts />
                    </Card>
                </>}
            </>}

            {this.state.MODEL === 'LIS' && <LabDashboard />}
        </div>;
    }
}

export default withParamsAndNavigate(NewDashboard);